import { defineStore } from "pinia";
import { store } from "../index";
import { UserLoginType, UserType, UserInfoType } from "@/api/login/types";
import { ElMessageBox } from "element-plus";
import { useI18n } from "@/hooks/web/useI18n";
import { loginOutApi } from "@/api/login";
import { useLocaleStoreWithOut } from "@/store/modules/locale";
import { useTagsViewStore } from "./tagsView";
import { usePermissionStore } from "./permission";
import { useCultureStoreWithOut } from "./culture";
import router, { resetRouter } from "@/router";
import type { Country } from "@/api/country";
import { DEFAULT_TIMEZONE_ID } from "@/config/timezone";
import { normalizeCultureLocale } from "@/utils/locale";
import service, { revokeSession } from "@/axios/service";
import { endIdleSession, isIdleSessionEnded } from "@/utils/idleSession";

interface UserState {
  userInfo?: UserInfoType;
  operInfo?: any; // 操作员信息
  authInfo?: any; // 权限信息
  tokenKey: string;
  token: string;
  publicKey: string;
  roleRouters?: string[] | AppCustomRouteRecordRaw[];
  rememberMe: boolean;
  loginInfo?: Pick<UserLoginType, "username">;
  imageServerUrl?: string;
  countryConfig?: Country; // 机构所属国家的配置信息
}

export const useUserStore = defineStore("user", {
  state: (): UserState => {
    // 从sessionStorage恢复用户信息（与Vue2项目保持一致）
    let userInfo: UserInfoType | undefined;
    let operInfo: any;
    let authInfo: any;
    let countryConfig: Country | undefined;

    try {
      const sessionUserInfo = sessionStorage.getItem("userInfo");
      if (sessionUserInfo) {
        userInfo = {
          oper: JSON.parse(sessionUserInfo),
          token: localStorage.getItem("token") || "",
          publicKey: localStorage.getItem("publicKey") || "",
        } as UserInfoType;
      }

      // 恢复操作员信息
      const sessionOperInfo = sessionStorage.getItem("operInfo");
      if (sessionOperInfo) {
        const parsedOperInfo = JSON.parse(sessionOperInfo);
        operInfo = {
          ...parsedOperInfo,
          culture: normalizeCultureLocale(parsedOperInfo?.culture),
        };
      }

      // 恢复权限信息
      const sessionAuthInfo = sessionStorage.getItem("authInfo");
      if (sessionAuthInfo) {
        authInfo = JSON.parse(sessionAuthInfo);
      }

      // 恢复国家配置
      const sessionCountryConfig = sessionStorage.getItem("countryConfig");
      if (sessionCountryConfig) {
        countryConfig = JSON.parse(sessionCountryConfig);
      }
    } catch (error) {
      console.warn("恢复用户信息失败:", error);
    }

    return {
      userInfo,
      operInfo,
      authInfo,
      tokenKey: "Authorization",
      token: localStorage.getItem("token") || "",
      publicKey: localStorage.getItem("publicKey") || "",
      roleRouters: undefined,
      // 记住我
      rememberMe: false,
      loginInfo: undefined,
      imageServerUrl: sessionStorage.getItem("imageServerUrl") || "",
      countryConfig,
    };
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey;
    },
    getToken(): string {
      return this.token;
    },
    getPublicKey(): string {
      return this.publicKey;
    },
    getUserInfo(): UserInfoType | undefined {
      return this.userInfo;
    },
    getOperInfo(): any {
      return this.operInfo;
    },
    getAuthInfo(): any {
      return this.authInfo;
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters;
    },
    getRememberMe(): boolean {
      return this.rememberMe;
    },
    getLoginInfo(): Pick<UserLoginType, "username"> | undefined {
      return this.loginInfo;
    },
    getImageServerUrl(): string {
      return this.imageServerUrl || "";
    },
    getCountryConfig(): Country | undefined {
      return this.countryConfig;
    },
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey;
    },
    setToken(token: string) {
      this.token = token;
      // 将token存储到localStorage
      if (token) {
        localStorage.setItem("token", token);
      } else {
        localStorage.removeItem("token");
      }
    },
    setPublicKey(publicKey: string) {
      this.publicKey = publicKey;
      if (publicKey) {
        localStorage.setItem("publicKey", publicKey);
      } else {
        localStorage.removeItem("publicKey");
      }
    },
    setUserInfo(userInfo?: UserInfoType) {
      this.userInfo = userInfo;
      if (userInfo) {
        const userOrgId = (userInfo.oper as { user_org_id?: number | null })?.user_org_id;
        if (userOrgId != null) localStorage.setItem("user_org_id", String(userOrgId));
        else localStorage.removeItem("user_org_id");
        sessionStorage.setItem("userInfo", JSON.stringify(userInfo.oper));
        if (userInfo.imageServerUrl) {
          this.imageServerUrl = userInfo.imageServerUrl;
          sessionStorage.setItem("imageServerUrl", userInfo.imageServerUrl);
        }
      } else {
        localStorage.removeItem("user_org_id");
        sessionStorage.removeItem("userInfo");
        sessionStorage.removeItem("imageServerUrl");
      }
    },
    setUserOrgId(userOrgId: number) {
      localStorage.setItem("user_org_id", String(userOrgId));
      if (this.userInfo) {
        this.userInfo.oper = { ...this.userInfo.oper, user_org_id: userOrgId };
        sessionStorage.setItem("userInfo", JSON.stringify(this.userInfo.oper));
      }
      if (this.operInfo) {
        this.operInfo = { ...this.operInfo, user_org_id: userOrgId };
        sessionStorage.setItem("operInfo", JSON.stringify(this.operInfo));
      }
    },
    setImageServerUrl(imageServerUrl: string) {
      this.imageServerUrl = imageServerUrl;
      sessionStorage.setItem("imageServerUrl", imageServerUrl);
    },
    setOperInfo(operInfo?: any) {
      const normalizedOperInfo = operInfo
        ? {
            ...operInfo,
            culture: normalizeCultureLocale(operInfo?.culture),
          }
        : operInfo;
      this.operInfo = normalizedOperInfo;
      const cultureStore = useCultureStoreWithOut();
      cultureStore.setTimezone(normalizedOperInfo?.timezone || DEFAULT_TIMEZONE_ID);
      if (normalizedOperInfo) {
        sessionStorage.setItem("operInfo", JSON.stringify(normalizedOperInfo));
      } else {
        sessionStorage.removeItem("operInfo");
      }
    },
    setAuthInfo(authInfo?: any) {
      this.authInfo = authInfo;
      if (authInfo) {
        sessionStorage.setItem("authInfo", JSON.stringify(authInfo));
      } else {
        sessionStorage.removeItem("authInfo");
      }
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters;
    },
    logoutConfirm() {
      const { t } = useI18n();
      ElMessageBox.confirm(
        t("common.messages.loginOutMessage"),
        t("common.dialogTitles.reminder"),
        {
          confirmButtonText: t("common.confirm"),
          cancelButtonText: t("common.cancel"),
          type: "warning",
        },
      )
        .then(async () => {
          try {
            const localeStore = useLocaleStoreWithOut();
            const currentLocale = localeStore.getCurrentLocale;
            await loginOutApi({ language: currentLocale.lang });
          } catch (error) {
            // 即使API调用失败，也要执行退出登录
            console.warn("退出登录API调用失败:", error);
          }
          this.reset();
        })
        .catch(() => {});
    },
    expireSession(notifyServer = false) {
      const token = this.token;
      if (!token) return;
      service.cancelAllRequest();
      if (notifyServer && !isIdleSessionEnded(token)) {
        // Capture/sign the request before reset clears the current credentials.
        revokeSession();
      }
      ElMessageBox.close();
      this.reset();
    },
    reset() {
      try {
        endIdleSession(this.token);
        const tagsViewStore = useTagsViewStore();
        const permissionStore = usePermissionStore();
        const cultureStore = useCultureStoreWithOut();

        tagsViewStore.delAllViews();

        this.setToken("");
        this.setPublicKey("");
        this.setUserInfo(undefined);
        this.setOperInfo(undefined);
        this.setAuthInfo(undefined);
        this.setRoleRouters([]);
        this.setImageServerUrl("");
        this.setCountryConfig(undefined);
        cultureStore.reset();

        // 清空权限数据
        permissionStore.setIsAddRouters(false);
        permissionStore.setMenuTabRouters([]);
        // 直接清空状态
        permissionStore.$patch({
          routers: [],
          addRouters: [],
        });

        resetRouter();

        // 强制跳转到登录页
        router.replace("/login");
      } catch (error) {
        console.error("reset方法执行出错:", error);
      }
    },
    logout() {
      this.reset();
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe;
    },
    setLoginInfo(loginInfo: Pick<UserLoginType, "username"> | undefined) {
      this.loginInfo = loginInfo;
    },
    setCountryConfig(countryConfig?: Country) {
      this.countryConfig = countryConfig;
      const cultureStore = useCultureStoreWithOut();
      cultureStore.setTimezone(this.operInfo?.timezone || DEFAULT_TIMEZONE_ID);
      if (countryConfig) {
        sessionStorage.setItem("countryConfig", JSON.stringify(countryConfig));
      } else {
        sessionStorage.removeItem("countryConfig");
      }
    },
  },
  persist: true,
});

export const useUserStoreWithOut = () => {
  return useUserStore(store);
};
